import {getCountryCallingCode} from 'react-phone-number-input'
import {dissocPath, omit} from 'ramda'

import {getConnection, getUserTier} from '../../../auth/modules/selectors'
import {User} from '../../../auth/types/Cognito'
import {Brand} from '../../../brand/types'
import {checkIsBrandVio} from '../../../brand/utils'
import {getDeviceCategory} from '../../../utils/userAgent'
import {Action, Category, Entity} from '../../types/Events'
import {getCio} from './getCio'
import {BookingContactDetails, EventPayload, TrackEvent} from './types'

const deviceInfo = () => ({
  deviceType: getDeviceCategory(),
  userAgent: navigator.userAgent
})

const getBookingPhoneNumber = (phone?: BookingContactDetails['phone']) => {
  if (!phone) return
  const countryCallingCode = getCountryCallingCode(phone.countryCode)

  return `+${countryCallingCode}${phone.number}`
}

const formatUserContactDetails = (
  bookingContactDetails?: BookingContactDetails | null,
  user?: User | null
) => {
  const {attributes} = user || {}

  if (!bookingContactDetails && !user) return

  return {
    firstName: attributes?.given_name || bookingContactDetails?.firstName,
    lastName: attributes?.family_name || bookingContactDetails?.lastName,
    phone:
      attributes?.phone_number ||
      getBookingPhoneNumber(bookingContactDetails?.phone),
    email: attributes?.email || bookingContactDetails?.email,
    bookingFirstName: bookingContactDetails?.firstName,
    bookingLastName: bookingContactDetails?.lastName,
    bookingEmail: bookingContactDetails?.email,
    bookingPhone: getBookingPhoneNumber(bookingContactDetails?.phone)
  }
}

const addOrUpdateUser: TrackEvent = ({
  user,
  country,
  language,
  anonymousId,
  bookingContactDetails,
  timezone
}) => {
  if (!user && !bookingContactDetails) return

  const userId = user?.attributes?.sub || bookingContactDetails?.email
  const authenticationMethod = user && getConnection(user)
  const tier = getUserTier(user)
  const userContactDetails = formatUserContactDetails(
    bookingContactDetails,
    user
  )

  const event: EventPayload = {
    ...(tier && {tier}),
    ...(country && {country}),
    ...(language && {language}),
    ...(timezone && {timezone}),
    ...(authenticationMethod && {authenticationMethod}),
    ...userContactDetails,
    ...deviceInfo()
  }

  getCio()?.identify({
    anonymous_id: anonymousId,
    id: userId,
    ...event
  })
}

const trackEvent: TrackEvent = ({...event}) => {
  const eventName = event?.name as string
  const payload = omit(['name'], event)

  if (payload?.userId) {
    getCio()?.track(eventName, {...payload})
    return
  }

  const userContactDetails =
    formatUserContactDetails(payload?.bookingContactDetails) || {}

  const removeDuplicatedBookingContactDetails = dissocPath<EventPayload>(
    ['bookingContactDetails'],
    payload
  )

  getCio()?.track(eventName, {
    ...removeDuplicatedBookingContactDetails,
    ...userContactDetails,
    ...deviceInfo()
  })
}

export const cioTrackPageViewName = (): string =>
  `${Category.System}_${Entity.Page}_${Action.Displayed}`

const trackPageView: TrackEvent = params =>
  trackEvent({
    ...params,
    name: cioTrackPageViewName()
  })

const emptyFn: () => void = () => undefined

const emptyCio = {
  addOrUpdateUser: emptyFn,
  trackEvent: emptyFn,
  trackPageView: emptyFn
}

export interface CustomerIoFunctions {
  addOrUpdateUser: TrackEvent
  trackEvent: TrackEvent
  trackPageView: TrackEvent
}

export const customerIo = (brand: Brand): CustomerIoFunctions => {
  // Cio should be triggered only when brand is Vio
  if (!checkIsBrandVio(brand)) {
    return emptyCio
  }

  return {
    addOrUpdateUser,
    trackEvent,
    trackPageView
  }
}
